import { For, Show, createEffect, createMemo, createSignal, onMount } from "solid-js";
import { useAppState } from "@core/state/AppState";
import { contentService, type FAQ } from "@core/services/providers/ContentService";
import { track } from "@core/services/analytics";

const formatDayLabel = (date: Date): string => {
  const today = new Date();
  const sameDay = date.toDateString() === today.toDateString();
  if (sameDay) return "Today";
  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);
  if (date.toDateString() === yesterday.toDateString()) {
    return "Yesterday";
  }
  return date.toLocaleDateString(undefined, { month: "short", day: "numeric" });
};

const formatTime = (date: Date): string =>
  date.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });

export function ChatPage() {
  const appState = useAppState();
  const state = appState.snapshot();

  const [draft, setDraft] = createSignal(state.composerDraft());
  const [faqCollapsed, setFaqCollapsed] = createSignal(true);
  const [faqs, setFaqs] = createSignal<FAQ[]>([]);
  let listRef: HTMLDivElement | undefined;
  let inputRef: HTMLTextAreaElement | undefined;

  // Load FAQs from Firestore on mount
  onMount(async () => {
    try {
      const loadedFaqs = await contentService.getFaqs();
      setFaqs(loadedFaqs);
    } catch (error) {
      console.error('Failed to load FAQs:', error);
    }
  });

  createEffect(() => {
    setDraft(state.composerDraft());
  });

  // Scroll to bottom when messages change
  createEffect(() => {
    const messages = state.messages();

    // Use setTimeout to ensure DOM has updated
    setTimeout(() => {
      if (listRef) {
        listRef.scrollTo({
          top: listRef.scrollHeight,
          behavior: 'smooth'
        });
      }
    }, 100);
  });

  const groupedMessages = createMemo(() => {
    const messages = state.messages();
    const groups: Array<{ label: string; items: typeof messages }> = [];
    let currentLabel: string | null = null;
    let currentItems: typeof messages = [];

    messages.forEach((message) => {
      const label = formatDayLabel(message.createdAt);
      if (label !== currentLabel) {
        if (currentItems.length > 0 && currentLabel) {
          groups.push({ label: currentLabel, items: currentItems });
        }
        currentLabel = label;
        currentItems = [];
      }
      currentItems.push(message);
    });

    if (currentItems.length > 0 && currentLabel) {
      groups.push({ label: currentLabel, items: currentItems });
    }
    return groups;
  });

  const handleInput = (event: InputEvent & { currentTarget: HTMLTextAreaElement }) => {
    const textarea = event.currentTarget;
    setDraft(textarea.value);
    appState.setComposerDraft(textarea.value);

    // Auto-resize textarea
    textarea.style.height = 'auto';
    textarea.style.height = textarea.scrollHeight + 'px';
  };

  const handleSubmit = async (event: Event) => {
    event.preventDefault();
    await appState.sendMessage(draft());
    inputRef?.focus();
  };

  const handleFaqClick = async (question: string) => {
    setDraft(question);
    appState.setComposerDraft(question);
    await appState.sendMessage(question);

    // Close FAQ section after selecting a question
    setFaqCollapsed(true);

    inputRef?.focus();
  };

  const isSendDisabled = () => {
    return !draft().trim() || state.isSendingMessage() || state.isOffline();
  };

  return (
    <>
      {/* Main container - matches template pattern */}
      <div class="main-container" ref={listRef}>
        <Show when={!state.isInitializingChat()} fallback={<div class="loading-state">Preparing your chat…</div>}>
          <Show when={state.messages().length > 0} fallback={<p class="chat-empty">No messages yet. Say hello!</p>}>
            <For each={groupedMessages()}>
              {(group) => (
                <div class="chat-day-group">
                  <div class="chat-day-label">{group.label}</div>
                  <For each={group.items}>
                    {(message) => {
                      const isClient = message.senderType === "user";
                      const actions = message.metadata?.actions;
                      return (
                        <div classList={{ "chat-message": true, "user": isClient, "bot": !isClient }}>
                          <div class="message-bubble">
                            <p class="message-text">{message.text}</p>
                            <Show when={actions && Array.isArray(actions) && actions.length > 0}>
                              <div class="message-actions">
                                <For each={actions}>
                                  {(action: any) => (
                                    <Show when={action.type === 'link' && action.url && action.label}>
                                      <a
                                        href={action.url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        class="message-action-button"
                                      >
                                        {action.label}
                                      </a>
                                    </Show>
                                  )}
                                </For>
                              </div>
                            </Show>
                          </div>
                          <span class="message-time">{formatTime(message.createdAt)}</span>
                        </div>
                      );
                    }}
                  </For>
                </div>
              )}
            </For>
          </Show>
        </Show>
      </div>

      {/* Footer - fixed at bottom like template */}
      <footer classList={{ "footer": true, "hidden": state.isInitializingChat() }}>
          {/* FAQ Section - only show if FAQs are loaded */}
          <Show when={faqs().length > 0}>
            <div class="faq-collapse-wrapper">
              <button
                class="faq-toggle-btn"
                onClick={() => setFaqCollapsed(!faqCollapsed())}
                aria-expanded={!faqCollapsed()}
                aria-label="Toggle FAQ questions"
              >
                <i classList={{ "fas": true, "fa-chevron-up": faqCollapsed(), "fa-chevron-down": !faqCollapsed(), "faq-toggle-icon": true }}></i>
              </button>
              <div classList={{ "suggested-questions": true, "collapsed": faqCollapsed() }}>
                <For each={faqs()}>
                  {(faq) => (
                    <button
                      class="question-btn"
                      onClick={() => handleFaqClick(faq.question)}
                    >
                      {faq.question}
                    </button>
                  )}
                </For>
              </div>
            </div>
          </Show>

          <form class="chat-input-container" onSubmit={handleSubmit}>
            <textarea
              ref={inputRef}
              class="chat-input"
              placeholder={state.isOffline() ? "You're offline – messages will send when reconnected" : "Type your message..."}
              value={draft()}
              onInput={handleInput}
              disabled={state.isOffline()}
              maxlength={500}
              rows={1}
            />
            <button type="submit" class="chat-send-btn" disabled={isSendDisabled()}>
              <i class="fa-solid fa-paper-plane"></i>
            </button>
          </form>
      </footer>
    </>
  );
}

